import React, { useState } from 'react';
import ConfirmModal from '../components/common/ConfirmModal';
import MainLayout from '../components/layout/MainLayout';
import { 
  useGetWhatsappPhonesQuery,
  useAddWhatsappPhoneMutation,
  useUpdateWhatsappPhoneMutation,
  useDeleteWhatsappPhoneMutation,
  useConnectWhatsappPhoneMutation,
  WhatsappPhone
} from '../store/services/whatsappApi';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useForm } from 'react-hook-form';

// Define the form data type for adding/editing a phone
interface PhoneFormData {
  phone_number: string;
  phone_number_id?: string;
  display_name: string;
  business_account_id?: string;
  access_token?: string;
  verify_token?: string;
  description?: string;
  webhook_url?: string;
  is_default?: boolean;
  is_active?: boolean;
}

const PhoneConfig: React.FC = () => {
  // State for modals and selected phone
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [selectedPhoneId, setSelectedPhoneId] = useState<number | null>(null);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const [pinValue, setPinValue] = useState<string>('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  // Confirm modals state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [phoneIdToDelete, setPhoneIdToDelete] = useState<number | null>(null);
  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [phoneIdToDisconnect, setPhoneIdToDisconnect] = useState<number | null>(null);
  
  // Fetch all phones
  const { 
    data: phones, 
    isLoading: isPhonesLoading, 
    isError: isPhonesError,
    refetch: refetchPhones
  } = useGetWhatsappPhonesQuery();
  
  // Fetch selected phone details
  const { 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    data: selectedPhone
  } = useGetWhatsappPhonesQuery(undefined, {
    selectFromResult: (result) => {
      return {
        data: selectedPhoneId ? result.data?.find(phone => phone.id === selectedPhoneId) : undefined
      };
    },
  });
  
  // Use refetch from the main query
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const refetchSelectedPhone = refetchPhones;
  
  // Mutations
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [createPhone, { isLoading: isCreating }] = useAddWhatsappPhoneMutation();
  const [updatePhone, { isLoading: isUpdating }] = useUpdateWhatsappPhoneMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deletePhone, { isLoading: isDeleting }] = useDeleteWhatsappPhoneMutation();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [connectPhone, { isLoading: isConnecting }] = useConnectWhatsappPhoneMutation();
  // These hooks are not currently available in the API
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isDisconnecting = false;
  const isRefreshingQR = false;
  const isVerifyingPin = false;
  
  // Form handling
  const { register, handleSubmit, reset, formState: { errors } } = useForm<PhoneFormData>();
  
  // Open add modal
  const openAddModal = () => {
    reset({
      phone_number: '',
      display_name: '',
      phone_number_id: '',
      business_account_id: '',
      access_token: '',
      verify_token: '',
      description: '',
      webhook_url: '',
      is_default: false,
      is_active: true
    });
    setIsAddModalOpen(true);
  };
  
  // Open edit modal
  const openEditModal = (id: number) => {
    setSelectedPhoneId(id);
    const phone = phones?.find((p: WhatsappPhone) => p.id === id);
    if (phone) {
      reset({
        phone_number: phone.phone_number,
        display_name: phone.display_name,
        phone_number_id: phone.phone_number_id || '',
        business_account_id: phone.business_account_id || '',
        access_token: phone.access_token || '',
        verify_token: phone.verify_token || '',
        description: phone.description || '',
        webhook_url: phone.webhook_url || '',
        is_default: phone.is_default || false,
        is_active: phone.is_active !== undefined ? phone.is_active : true
      });
      setIsEditModalOpen(true);
    }
  };
  
  // Open QR code modal
  const openQRModal = async (id: number) => {
    setSelectedPhoneId(id);
    // Function commented out due to API updates
    toast.info('QR code functionality is temporarily unavailable');
    // Placeholder for development
    setQrCodeUrl('placeholder-qr-code');
    setIsQRModalOpen(true);
  };
  
  // Handle copy to clipboard
  const handleCopy = (text: string) => {
    setCopiedText(text);
    setTimeout(() => setCopiedText(null), 2000);
  };
  
  // Handle add phone submission
  const handleAddSubmit = async (data: PhoneFormData) => {
    try {
      await createPhone(data).unwrap();
      toast.success('Phone added successfully');
      setIsAddModalOpen(false);
      refetchPhones();
    } catch (error) {
      toast.error('Failed to add phone');
    }
  };
  
  // Handle edit phone submission
  const handleEditSubmit = async (data: PhoneFormData) => {
    if (selectedPhoneId) {
      try {
        await updatePhone({ id: selectedPhoneId, ...data }).unwrap();
        toast.success('Phone updated successfully');
        setIsEditModalOpen(false);
        refetchPhones();
      } catch (error) {
        toast.error('Failed to update phone');
      }
    }
  };
  
  // Handle delete phone
  const handleDeletePhone = (id: number) => {
    setPhoneIdToDelete(id);
    setIsDeleteModalOpen(true);
  };
  const confirmDeletePhone = async () => {
    if (phoneIdToDelete == null) return;
    try {
      await deletePhone(phoneIdToDelete).unwrap();
      toast.success('Phone deleted successfully');
      setIsDeleteModalOpen(false);
      setPhoneIdToDelete(null);
      refetchPhones();
    } catch (error) {
      toast.error('Failed to delete phone');
    }
  };
  
  // Handle connect phone
  const handleConnect = async (id: number) => {
    try {
      await connectPhone(id).unwrap();
      openQRModal(id);
      refetchPhones();
    } catch (error) {
      toast.error('Failed to initiate connection');
    }
  };
  
  // Handle disconnect phone
  const handleDisconnect = (id: number) => {
    setPhoneIdToDisconnect(id);
    setIsDisconnectModalOpen(true);
  };
  const confirmDisconnectPhone = async () => {
    if (phoneIdToDisconnect == null) return;
    // Function commented out due to API updates
    toast.info('Disconnect functionality is temporarily unavailable');
    setIsDisconnectModalOpen(false);
    setPhoneIdToDisconnect(null);
  };
  
  // Handle refresh QR code
  const handleRefreshQR = async () => {
    // Function commented out due to API updates
    toast.info('QR refresh functionality is temporarily unavailable');
  };
  
  // Handle PIN verification
  const handleVerifyPin = async () => {
    // Function commented out due to API updates
    toast.info('PIN verification functionality is temporarily unavailable');
    setIsQRModalOpen(false);
    setPinValue('');
  };
  
  // Helper function to get status badge color
  const getStatusBadgeColor = (status: string, connected: boolean | undefined) => {
    if (connected) return 'bg-green-100 text-green-800';
    
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Loading state
  if (isPhonesLoading) {
    return (
      <MainLayout>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">WhatsApp Phones</h1>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  // Error state
  if (isPhonesError) {
    return (
      <MainLayout>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">WhatsApp Phones</h1>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <p>Failed to load WhatsApp phones. Please try again later.</p>
          </div>
        </div>
      </MainLayout>
    );
  }
  
  // Render phones list
  return (
    <MainLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">WhatsApp Phones</h1>
          <button
            className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            onClick={openAddModal}
          >
            Add Phone
          </button>
        </div>
        
        {phones && phones.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {phones.map((phone: WhatsappPhone) => (
              <div key={phone.id} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold">{phone.display_name}</h2>
                  <div className="flex items-center">
                    <CopyToClipboard text={phone.phone_number}>
                      <button
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => handleCopy(phone.phone_number)}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                        </svg>
                      </button>
                    </CopyToClipboard>
                    {copiedText === phone.phone_number && (
                      <span className="ml-2 text-xs text-green-600">Copied!</span>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeColor(phone.status || '', phone.connected)}`}>
                  {phone.connected ? 'Connected' : (phone.status ? phone.status.charAt(0).toUpperCase() + phone.status.slice(1) : 'Unknown')}
                </span>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Created:</span>
                    <span>{new Date(phone.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">Last Updated:</span>
                    <span>{new Date(phone.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="mt-6 flex justify-between">
                  <div className="space-x-2">
                    {!phone.connected && (
                      <button className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white py-1 px-3 rounded text-sm" onClick={() => handleConnect(phone.id)}>
                        Connect
                      </button>
                    )}
                    <button className="bg-gray-200 hover:bg-gray-300 text-gray-800 py-1 px-3 rounded text-sm" onClick={() => openEditModal(phone.id)}>
                      Edit
                    </button>
                    <button className="bg-red-100 hover:bg-red-200 text-red-800 py-1 px-3 rounded text-sm" onClick={() => handleDeletePhone(phone.id)}>
                      Delete
                    </button>
                  </div>
                  {phone.connected && (
                    <button className="bg-red-100 hover:bg-red-200 text-red-800 py-1 px-3 rounded text-sm" onClick={() => handleDisconnect(phone.id)}>
                      Disconnect
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="col-span-full bg-gray-50 rounded-lg p-6 text-center">
            <p className="text-gray-500 mb-4">No WhatsApp phones configured yet.</p>
            <button
              className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              onClick={openAddModal}
            >
              Add Phone
            </button>
          </div>
        )}
      </div>
      
      {/* Add Phone Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Add WhatsApp Phone</h2>
              <button
                className="text-gray-500 hover:text-gray-700"
                onClick={() => setIsAddModalOpen(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit(handleAddSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Phone Number */}
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone_number">
                    Phone Number*
                  </label>
                  <input
                    className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.phone_number ? 'border-red-500' : ''}`}
                    id="phone_number"
                    type="text"
                    placeholder="+1234567890"
                    {...register('phone_number', { required: 'Phone number is required' })}
                  />
                  {errors.phone_number && <p className="text-red-500 text-xs italic">{errors.phone_number.message}</p>}
                </div>
                
                {/* Display Name */}
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="display_name">
                    Display Name*
                  </label>
                  <input
                    className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.display_name ? 'border-red-500' : ''}`}
                    id="display_name"
                    type="text"
                    placeholder="My WhatsApp"
                    {...register('display_name', { required: 'Display name is required' })}
                  />
                  {errors.display_name && <p className="text-red-500 text-xs italic">{errors.display_name.message}</p>}
                </div>
              </div>
              
              {/* WhatsApp Business API Fields */}
              <div className="mb-6 border-t border-gray-200 pt-4">
                <h3 className="text-md font-medium text-gray-700 mb-3">WhatsApp Business API Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Phone Number ID */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone_number_id">
                      Phone Number ID
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="phone_number_id"
                      type="text"
                      placeholder="WhatsApp Phone Number ID"
                      {...register('phone_number_id')}
                    />
                  </div>
                  
                  {/* Business Account ID */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="business_account_id">
                      Business Account ID
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="business_account_id"
                      type="text"
                      placeholder="WhatsApp Business Account ID"
                      {...register('business_account_id')}
                    />
                  </div>
                  
                  {/* Access Token */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="access_token">
                      Access Token
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="access_token"
                      type="password"
                      placeholder="WhatsApp API Access Token"
                      {...register('access_token')}
                    />
                  </div>
                  
                  {/* Verify Token */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="verify_token">
                      Verify Token
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="verify_token"
                      type="text"
                      placeholder="Webhook Verify Token"
                      {...register('verify_token')}
                    />
                  </div>
                </div>
              </div>
              
              {/* Additional Fields */}
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Webhook URL */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhook_url">
                      Webhook URL
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="webhook_url"
                      type="text"
                      placeholder="https://your-webhook-url.com"
                      {...register('webhook_url')}
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                      Description
                    </label>
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      id="description"
                      type="text"
                      placeholder="Phone description"
                      {...register('description')}
                    />
                  </div>
                </div>
              </div>
              
              {/* Checkboxes */}
              <div className="mb-6 flex space-x-6">
                <div className="flex items-center">
                  <input
                    id="is_default"
                    type="checkbox"
                    className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                    {...register('is_default')}
                  />
                  <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700">
                    Set as default phone
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    id="is_active"
                    type="checkbox"
                    className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                    {...register('is_active')}
                  />
                  <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                    Active
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  onClick={() => setIsAddModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  disabled={isCreating}
                >
                  {isCreating ? 'Adding...' : 'Add Phone'}
                </button>
              </div>
              {/* Close the Add Phone form */}
              </form>
            </div>
          </div>
        )}
        
        {/* Edit Phone Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Edit WhatsApp Phone</h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setIsEditModalOpen(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <form onSubmit={handleSubmit(handleEditSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Phone Number */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone_number">
                      Phone Number*
                    </label>
                    <input
                      className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.phone_number ? 'border-red-500' : ''}`}
                      id="phone_number"
                      type="text"
                      placeholder="+1234567890"
                      {...register('phone_number', { required: 'Phone number is required' })}
                    />
                    {errors.phone_number && <p className="text-red-500 text-xs italic">{errors.phone_number.message}</p>}
                  </div>
                  
                  {/* Display Name */}
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="display_name">
                      Display Name*
                    </label>
                    <input
                      className={`shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${errors.display_name ? 'border-red-500' : ''}`}
                      id="display_name"
                      type="text"
                      placeholder="My WhatsApp"
                      {...register('display_name', { required: 'Display name is required' })}
                    />
                    {errors.display_name && <p className="text-red-500 text-xs italic">{errors.display_name.message}</p>}
                  </div>
                </div>
                
                {/* WhatsApp Business API Fields */}
                <div className="mb-6 border-t border-gray-200 pt-4">
                  <h3 className="text-md font-medium text-gray-700 mb-3">WhatsApp Business API Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Phone Number ID */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phone_number_id">
                        Phone Number ID
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="phone_number_id"
                        type="text"
                        placeholder="WhatsApp Phone Number ID"
                        {...register('phone_number_id')}
                      />
                    </div>
                    
                    {/* Business Account ID */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="business_account_id">
                        Business Account ID
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="business_account_id"
                        type="text"
                        placeholder="WhatsApp Business Account ID"
                        {...register('business_account_id')}
                      />
                    </div>
                    
                    {/* Access Token */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="access_token">
                        Access Token
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="access_token"
                        type="password"
                        placeholder="WhatsApp API Access Token"
                        {...register('access_token')}
                      />
                    </div>
                    
                    {/* Verify Token */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="verify_token">
                        Verify Token
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="verify_token"
                        type="text"
                        placeholder="Webhook Verify Token"
                        {...register('verify_token')}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Additional Fields */}
                <div className="mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Webhook URL */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="webhook_url">
                        Webhook URL
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="webhook_url"
                        type="text"
                        placeholder="https://your-webhook-url.com"
                        {...register('webhook_url')}
                      />
                    </div>
                    
                    {/* Description */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                        Description
                      </label>
                      <input
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        id="description"
                        type="text"
                        placeholder="Phone description"
                        {...register('description')}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Checkboxes */}
                <div className="mb-6 flex space-x-6">
                  <div className="flex items-center">
                    <input
                      id="is_default"
                      type="checkbox"
                      className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      {...register('is_default')}
                    />
                    <label htmlFor="is_default" className="ml-2 block text-sm text-gray-700">
                      Set as default phone
                    </label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      id="is_active"
                      type="checkbox"
                      className="h-4 w-4 text-whatsapp-green focus:ring-whatsapp-green border-gray-300 rounded"
                      {...register('is_active')}
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
                      Active
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2">
                  <button
                    type="button"
                    className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    onClick={() => setIsEditModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {/* QR Code Modal */}
        {isQRModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Scan QR Code</h2>
                <button
                  className="text-gray-500 hover:text-gray-700"
                  onClick={() => setIsQRModalOpen(false)}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="text-center">
                {qrCodeUrl ? (
                  <div className="mb-4">
                    <img src={qrCodeUrl} alt="WhatsApp QR Code" className="mx-auto" />
                    <button
                      className="mt-4 bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      onClick={handleRefreshQR}
                      disabled={isRefreshingQR}
                    >
                      {isRefreshingQR ? 'Refreshing...' : 'Refresh QR Code'}
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-whatsapp-green"></div>
                  </div>
                )}
                
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Or Enter PIN</h3>
                  <div className="flex space-x-2">
                    <input
                      className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                      type="text"
                      placeholder="Enter PIN"
                      value={pinValue}
                      onChange={(e) => setPinValue(e.target.value)}
                    />
                    <button
                      className="bg-whatsapp-green hover:bg-whatsapp-dark-green text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                      onClick={handleVerifyPin}
                      disabled={isVerifyingPin || !pinValue.trim()}
                    >
                      {isVerifyingPin ? 'Verifying...' : 'Verify'}
                    </button>
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 mt-4">
                  Note: The QR code will expire after 60 seconds. Refresh it if needed.
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Confirm delete phone */}
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Delete Phone"
          message="Are you sure you want to delete this phone? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          destructive
          onConfirm={confirmDeletePhone}
          onCancel={() => { setIsDeleteModalOpen(false); setPhoneIdToDelete(null); }}
        />

        {/* Confirm disconnect phone */}
        <ConfirmModal
          isOpen={isDisconnectModalOpen}
          title="Disconnect Phone"
          message="Are you sure you want to disconnect this phone?"
          confirmText="Disconnect"
          cancelText="Cancel"
          destructive
          onConfirm={confirmDisconnectPhone}
          onCancel={() => { setIsDisconnectModalOpen(false); setPhoneIdToDisconnect(null); }}
        />
    </MainLayout>
  );
};

export default PhoneConfig;
